import {
  Box,
  Button,
  Card,
  CardContent,
  CircularProgress,
  Typography,
} from "@mui/material";
import { useEffect, useMemo, useRef, useState } from "react";
import { useLocation } from "react-router-dom";
import Logo from "assets/images/side_nav_inkfree_logo.svg";
import AuthLayout from "layouts/AuthLayout/AuthLayout";
import { useAppSelector } from "hooks/use-app-select";
import { selectAuthenticated, selectEmail } from "redux/authSlice";
import { mobile, signer, signerAccentBar, info } from "theme/colors";
import { authRef, refFont } from "screens/Auth/AuthReferenceStyles";
import viewpaths from "global/constants/ViewPathConstants";
import urls from "global/constants/UrlConstants";
import history from "utils/history";
import { useTitle } from "utils/UseTitle";
import title from "global/constants/TitleConstants";
import {
  describeScope,
  parseScopes,
  ScopeIconKind,
} from "./McpTypesAndValidation";
import { getMcpConsentClientInfo } from "./McpService";

const cardSx = {
  width: "100%",
  maxWidth: 480,
  mx: "auto",
  borderRadius: "16px",
  border: `1px solid ${authRef.cardBorder}`,
  boxShadow: authRef.cardShadow,
  padding: { xs: "28px 24px 24px", sm: "36px 40px 32px" },
  boxSizing: "border-box",
} as const;

const ICON_TONE: Record<ScopeIconKind, { bg: string; fg: string }> = {
  write: { bg: signer.purple.tint, fg: signer.purple.main },
  send: { bg: signer.orange.tint, fg: signer.orange.main },
  read: { bg: info.light, fg: info.main },
  identity: { bg: authRef.badgeBgPurple, fg: signer.purple.main },
};

const ScopeIcon = ({ kind }: { kind: ScopeIconKind }) => {
  const paths: Record<ScopeIconKind, JSX.Element> = {
    write: <path d="M12 5v14M5 12h14" strokeLinecap="round" />,
    send: <path d="M22 2 11 13M22 2l-7 20-4-9-9-4 20-7z" strokeLinejoin="round" />,
    read: (
      <>
        <path d="M1 12s4-7 11-7 11 7 11 7-4 7-11 7-11-7-11-7z" />
        <circle cx="12" cy="12" r="3" />
      </>
    ),
    identity: (
      <>
        <path d="M12 3l7 3v5c0 4.5-3 8.2-7 9.5-4-1.3-7-5-7-9.5V6l7-3z" />
        <path d="M9.5 12l2 2 3.5-3.5" />
      </>
    ),
  };
  return (
    <svg
      aria-hidden="true"
      viewBox="0 0 24 24"
      fill="none"
      stroke="currentColor"
      strokeWidth="1.8"
      strokeLinecap="round"
      strokeLinejoin="round"
      width="18"
      height="18"
    >
      {paths[kind]}
    </svg>
  );
};

/**
 * Mirrors ss-mailzzy-web's OAuthConsent: an authenticated-only page (bounces
 * to Login with `?redirectionUrl=` if hit cold), a friendly client name
 * looked up via the shared /mcp/consent/client-info endpoint, and two hidden
 * forms (Deny submits without scope fields, which the backend treats as a
 * denial; Approve submits one `scope` field per requested scope).
 */
const OAuthConsent = () => {
  useTitle(title.OAUTH_CONSENT);
  const location = useLocation();
  const isLoggedIn = useAppSelector(selectAuthenticated);
  const email = useAppSelector(selectEmail);

  const [clientName, setClientName] = useState("");
  const [submitting, setSubmitting] = useState(false);

  const approveFormRef = useRef<HTMLFormElement>(null);
  const denyFormRef = useRef<HTMLFormElement>(null);

  const { clientId, state, scopes } = useMemo(() => {
    const params = new URLSearchParams(location.search);
    return {
      clientId: params.get("client_id") ?? "",
      state: params.get("state") ?? "",
      scopes: parseScopes(params.get("scope")),
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [location.search]);

  useEffect(() => {
    if (!isLoggedIn) {
      const currentPath = location.pathname + location.search;
      history.replace(
        `${viewpaths.loginViewPath}?redirectionUrl=${encodeURIComponent(currentPath)}`,
      );
    }
  }, [isLoggedIn, location.pathname, location.search]);

  useEffect(() => {
    if (!isLoggedIn || !clientId) return;
    let active = true;
    getMcpConsentClientInfo(clientId)
      .then((clientInfo) => {
        if (active && clientInfo?.clientName) setClientName(clientInfo.clientName);
      })
      .catch(() => {
        /* keep the generic "An application" fallback */
      });
    return () => {
      active = false;
    };
  }, [isLoggedIn, clientId]);

  if (!isLoggedIn) return null;

  const isValidRequest = Boolean(clientId) && Boolean(state);

  const renderInvalid = () => (
    <>
      <Typography
        component="h1"
        sx={{
          color: mobile.base[500],
          fontSize: refFont.cardHeading,
          fontWeight: 700,
          textAlign: "center",
          mt: 3,
        }}
      >
        Nothing to authorize
      </Typography>
      <Typography
        sx={{
          color: authRef.subText,
          fontSize: refFont.cardBody,
          lineHeight: 1.6,
          textAlign: "center",
          mt: 1.5,
        }}
      >
        This page only works when you're sent here from an MCP client trying
        to connect to Inkfree.
      </Typography>
      <Button
        fullWidth
        sx={{
          mt: 3,
          height: "48px",
          borderRadius: "8px",
          fontWeight: 700,
          textTransform: "none",
          fontSize: refFont.cardButton,
        }}
        variant="contained"
        onClick={() => history.push(viewpaths.dashboardViewPath)}
      >
        Go to Inkfree
      </Button>
    </>
  );

  const renderConsent = () => (
    <>
      <Typography
        component="h1"
        sx={{
          color: mobile.base[500],
          fontSize: refFont.cardHeading,
          fontWeight: 700,
          textAlign: "center",
          mt: 3,
        }}
      >
        Authorize access
      </Typography>
      <Typography
        sx={{
          color: authRef.subText,
          fontSize: refFont.cardBody,
          lineHeight: 1.6,
          textAlign: "center",
          mt: 1,
        }}
      >
        <Box component="strong" sx={{ color: mobile.base[500] }}>
          {clientName || "An application"}
        </Box>{" "}
        is asking to connect to your Inkfree account{email ? ` (${email})` : ""}.
      </Typography>

      {scopes.length > 0 && (
        <>
          <Typography
            sx={{
              width: "100%",
              mt: 3,
              fontSize: refFont.divider,
              fontWeight: 700,
              letterSpacing: "0.06em",
              color: authRef.metaText,
              textTransform: "uppercase",
            }}
          >
            This will allow it to
          </Typography>

          <Box
            sx={{
              width: "100%",
              mt: 1.25,
              border: `1px solid ${authRef.cardBorder}`,
              borderRadius: "12px",
              overflow: "hidden",
            }}
          >
            {scopes.map((scope, index) => {
              const copy = describeScope(scope);
              const tone = ICON_TONE[copy.icon];
              return (
                <Box
                  key={scope}
                  sx={{
                    display: "flex",
                    gap: 1.5,
                    alignItems: "flex-start",
                    padding: "14px 16px",
                    borderTop: index === 0 ? "none" : `1px solid ${authRef.cardBorder}`,
                  }}
                >
                  <Box
                    aria-hidden="true"
                    sx={{
                      width: 32,
                      height: 32,
                      borderRadius: "8px",
                      flexShrink: 0,
                      backgroundColor: tone.bg,
                      color: tone.fg,
                      display: "flex",
                      alignItems: "center",
                      justifyContent: "center",
                    }}
                  >
                    <ScopeIcon kind={copy.icon} />
                  </Box>
                  <Box sx={{ minWidth: 0 }}>
                    <Typography
                      sx={{
                        fontSize: refFont.cardBody,
                        fontWeight: 700,
                        color: mobile.base[500],
                      }}
                    >
                      {copy.label}
                    </Typography>
                    <Typography
                      sx={{
                        mt: 0.25,
                        fontSize: refFont.link,
                        color: authRef.subText,
                        lineHeight: 1.5,
                      }}
                    >
                      {copy.detail}
                    </Typography>
                  </Box>
                </Box>
              );
            })}
          </Box>
        </>
      )}

      <Box sx={{ display: "flex", gap: 1.5, width: "100%", mt: 3 }}>
        <Button
          fullWidth
          variant="outlined"
          disabled={submitting}
          onClick={() => {
            setSubmitting(true);
            denyFormRef.current?.submit();
          }}
          sx={{
            height: "48px",
            borderRadius: "8px",
            fontWeight: 700,
            textTransform: "none",
            fontSize: refFont.cardButton,
            border: `1px solid ${authRef.inputBorder}`,
            color: mobile.base[500],
            "&:hover": { border: `1px solid ${authRef.inputBorderHover}` },
          }}
        >
          Deny
        </Button>
        <Button
          fullWidth
          variant="contained"
          disabled={submitting}
          startIcon={
            submitting ? <CircularProgress size={16} color="inherit" /> : undefined
          }
          onClick={() => {
            setSubmitting(true);
            approveFormRef.current?.submit();
          }}
          sx={{
            height: "48px",
            borderRadius: "8px",
            fontWeight: 700,
            textTransform: "none",
            fontSize: refFont.cardButton,
          }}
        >
          {submitting ? "Authorizing..." : "Authorize"}
        </Button>
      </Box>
    </>
  );

  const authorizeUrl = urls.MCP_OAUTH_AUTHORIZE;

  return (
    <AuthLayout>
      <Box>
        <Box
          aria-hidden="true"
          sx={{
            position: "fixed",
            top: 0,
            left: 0,
            right: 0,
            height: "5px",
            background: signerAccentBar,
            zIndex: (theme) => theme.zIndex.appBar,
          }}
        />
        <Card elevation={0} sx={cardSx}>
          <CardContent sx={{ p: 0, "&:last-child": { pb: 0 } }}>
            <Box sx={{ display: "flex", flexDirection: "column", alignItems: "center" }}>
              <Box
                component="img"
                src={Logo}
                alt="Inkfree e-Sign"
                sx={{ width: 106, height: "auto", display: "block" }}
              />
              {isValidRequest ? renderConsent() : renderInvalid()}
            </Box>
          </CardContent>
        </Card>
      </Box>

      {isValidRequest && (
        <>
          <form
            ref={approveFormRef}
            method="POST"
            action={authorizeUrl}
            style={{ display: "none" }}
          >
            <input type="hidden" name="client_id" value={clientId} />
            <input type="hidden" name="state" value={state} />
            {scopes.map((scope) => (
              <input key={scope} type="hidden" name="scope" value={scope} />
            ))}
          </form>

          <form
            ref={denyFormRef}
            method="POST"
            action={authorizeUrl}
            style={{ display: "none" }}
          >
            <input type="hidden" name="client_id" value={clientId} />
            <input type="hidden" name="state" value={state} />
          </form>
        </>
      )}
    </AuthLayout>
  );
};

export default OAuthConsent;
